import { useUpdateUserRole, useUserActiveOrganization } from "@/api";
import { Button } from "@/components/ui/button";
import Dialog from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Role } from "@/gql/__generated__/graphql";
import { DialogClose } from "@radix-ui/react-dialog";
import { useState } from "react";
import { MemberTable } from "../types";

const ROLES = Object.entries(Role).map(([k, v]) => ({
  displayValue: k,
  value: v
}));

interface Props {
  member: MemberTable;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function EditRole({ member, open, setOpen }: Props) {
  const [selectedRole, setSelectedRole] = useState(member.role);
  const [updateUserRole, { loading }] = useUpdateUserRole(member.id);
  const { activeOrganization, loading: loadingOrganization } =
    useUserActiveOrganization();
  const { toast } = useToast();

  const errorToast = (desc: string) => {
    toast({
      title: "Update role failed",
      description: (
        <>
          An error occurred when trying to change the role of {member.name} to{" "}
          {selectedRole.toLocaleLowerCase()} <br />
          {desc}{" "}
        </>
      ),
      variant: "destructive"
    });
  };

  const saveRole = async () => {
    if (loading) return;
    if (!activeOrganization) {
      console.error("organization not loaded");
      errorToast("Organization was not loaded. Please report this issue.");
      return;
    }

    updateUserRole({
      variables: {
        userId: member.userId,
        role: selectedRole,
        organizationId: activeOrganization.id
      }
    })
      .then((response) => {
        const data = response.data?.organizationMemberEditRole;

        if (data?.__typename === "OrganizationNotFound")
          errorToast("We could not find the organization.");
        else if (data?.__typename === "UserNotFound")
          errorToast("We could not find the user.");

        if (data?.__typename !== "OrganizationMembership")
          setSelectedRole(member.role);

        return response;
      })
      .catch((err) => {
        console.error(err);
        setSelectedRole(member.role);
        errorToast(
          "Unexpected error! Please report the issue if you encounter it again."
        );
      });
    setOpen(false);
  };

  const cancel = () => {
    setOpen(false);
    setSelectedRole(member.role);
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Content
        data-testid="editRoleDialogContent"
        className="sm:max-w-[425px] dark:bg-brand-gray-900"
      >
        <Dialog.Header className="space-y-2 relative">
          <Dialog.Title className="leading-5 text-brand-gray-800 dark:text-brand-gray-100 ">
            Edit role
          </Dialog.Title>
          <Dialog.Description className="text-brand-gray-700 dark:text-brand-gray-300">
            Select a role for {member.name} within the organization.
          </Dialog.Description>
        </Dialog.Header>
        <div className="flex items-center gap-4 mt-4">
          <Label
            htmlFor="role"
            className="text-right text-brand-gray-700 dark:text-brand-gray-100"
          >
            Role
          </Label>
          <Select
            name="role"
            value={selectedRole}
            onValueChange={(r) => setSelectedRole(r as Role)}
            disabled={loadingOrganization}
          >
            <SelectTrigger
              data-testid="roleSelectTrigger"
              className="dark:border-brand-gray-700"
              defaultValue={selectedRole}
            >
              {loadingOrganization ? (
                <LoadingCircle />
              ) : (
                <SelectValue placeholder="Select a role" />
              )}
            </SelectTrigger>
            <SelectContent className="dark:bg-brand-gray-900 dark:border-brand-gray-700">
              <SelectGroup>
                <SelectLabel>Roles</SelectLabel>
                {ROLES.map((role) => (
                  <SelectItem
                    data-testid="roleOption"
                    key={role.value}
                    value={role.value}
                    className="capitalize"
                  >
                    {role.displayValue}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <Dialog.Footer>
          <DialogClose asChild>
            <Button
              type="button"
              variant="secondary"
              onClick={cancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="default"
            type="submit"
            disabled={
              member.role === selectedRole || loading || loadingOrganization
            }
            onClick={saveRole}
          >
            Save
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
